/**
 * 🔒 How a line table longer than Region 8 is split across sheets (piece 27):
 * a server-side estimate of how many rows fit, made from Region 8's own height
 * and each row's measured line count, so the deterministic fake and the browser
 * behind the seam agree about which rows land on which sheet.
 *
 * 🔨 The estimate is the one place the split is decided. A renderer that let
 * the engine break the table where it liked would put a different row at the
 * head of sheet two in the fake than in the browser, and the golden fixtures
 * would prove nothing about the sheet a customer holds.
 */
import type { PrintLine, PrintLineColumn } from '../documents/printRegions';
import { printDrawnColumns, printMergesDescription } from '../documents/printRegions';
import { t } from '../strings';
import { richTextBlocks, richTextPlain, sanitizeRichText } from '../controls/richText';
import type { PrintTerms, PrintTermsClause } from '../settings/termContainers';

/** Typographic points in an inch, which is what the sheet's type is measured in. */
export const POINTS_PER_INCH = 72;

/**
 * 🔒 The line table's type and spacing, in one place (piece 27). The renderer's
 * own stylesheet reads these, and so does the estimate below, because a split
 * computed from one set of numbers and drawn with another is a split that puts
 * a row half off the sheet.
 */
export const PRINT_TABLE_METRICS = {
  /** Body text: the item's own name and every unmerged cell. */
  bodyPoints: 10,
  /** A cell's second line: a description under its item, a unit under its quantity. */
  subPoints: 8.5,
  /** The charcoal heading bar's own text. */
  headPoints: 9.5,
  /** The sheet's line height, the one the body sets. */
  lineHeight: 1.35,
  /** A cell's padding above and below, the heading bar's included. */
  cellPaddingPoints: 5,
  /** The heading bar's own padding, which is tighter than a cell's. */
  headPaddingPoints: 5,
  /**
   * 💰 The lines the heading bar itself draws on. The seeded Transactional set
   * wraps "MFR Part #" and "Unit Price" at Region 8's own width, so the bar is
   * two lines on the sheet that ships. Measured at one it left the eighth row
   * half off the page, which is the defect this estimate exists to end.
   */
  headingLines: 2,
  /** The hairline between two rows. */
  hairlinePoints: 0.4,
  /** A cell's padding left and right, which every column pays for. */
  cellSidePaddingPoints: 3,
  /** An average character's width as a share of its own point size. */
  characterWidthRatio: 0.5,
} as const;

/** The line table's metrics as numbers, so a template's own body size can scale them (decision 0656). */
export type PrintTableMetrics = { readonly [K in keyof typeof PRINT_TABLE_METRICS]: number };

const twoPlaces = (value: number) => Math.round(value * 100) / 100;

/**
 * 🔒 The line table's metrics at a template's own body size (decision 0656).
 * The three type sizes scale together and the spacing does not, so a sheet at
 * ten points is exactly `PRINT_TABLE_METRICS` and the split and the drawing
 * read one set of numbers at every size.
 */
export function printTableMetricsAt(bodyPoints: number): PrintTableMetrics {
  const base = PRINT_TABLE_METRICS;
  if (bodyPoints === base.bodyPoints) return base;
  const scale = bodyPoints / base.bodyPoints;
  return { ...base, bodyPoints, subPoints: twoPlaces(base.subPoints * scale), headPoints: twoPlaces(base.headPoints * scale) };
}

/**
 * What a template changes about how its table is measured (decision 0656):
 * the metrics at its body size, the columns' own shares of the width where it
 * set any, and whether the Qty cell stacks its unit.
 */
export interface PrintTableOptions {
  metrics?: PrintTableMetrics;
  /** Each drawn column's share of the table in percent, summing to a hundred; absent lets the table lay itself out. */
  widths?: Partial<Record<PrintLineColumn, number>> | null;
  showUnit?: boolean;
  /** 🔒 Whether the Unit Price cell stacks a sales line's list price beneath it (decision 1063); on unless the template hides it. */
  showListPrice?: boolean;
  /** 🔒 Whether the document's last sheet carries the taxable legend beneath the table (decision 1062), which the split holds room for. */
  taxLegend?: boolean;
  /** 🔒 Headings the document names for its own columns, such as a Sent payment's Bill (T3754); a template's typed heading still wins. */
  headings?: Partial<Record<PrintLineColumn, string>>;
}

/**
 * 💰 The characters a column that does not flex is expected to hold on one
 * line. The browser lays the table out and the server cannot ask it, so these
 * are read off a rendered sheet rather than computed from one, and a value
 * longer than its column's own figure is counted as wrapping.
 */
export const PRINT_COLUMN_CHARACTERS: Readonly<Partial<Record<PrintLineColumn, number>>> = {
  lineNumber: 4,
  mfrPart: 12,
  mfrReference: 14,
  qty: 6,
  unit: 6,
  unitPrice: 12,
  discount: 8,
  amount: 12,
  image: 10,
  asset: 18,
  hours: 6,
  level: 10,
  rate: 12,
  invoice: 14,
  settled: 12,
  entryDate: 12,
  document: 14,
  charge: 12,
  credit: 12,
  balance: 12,
  unitCost: 12,
  extendedCost: 12,
};

/** What a column nobody measured is assumed to hold, so a new column is never assumed infinite. */
export const PRINT_DEFAULT_COLUMN_CHARACTERS = 12;

/**
 * 🔒 The column that takes the width the others leave: the merged Item cell
 * where the set names both Item and Description, and a Description column of
 * its own everywhere else. The Field family, the statement and the receipt all
 * draw a description without an item, and a set naming neither flexes nothing.
 */
export function printFlexibleColumn(columns: readonly PrintLineColumn[]): PrintLineColumn | null {
  if (printMergesDescription(columns)) return 'item';
  return printDrawnColumns(columns).includes('description') ? 'description' : null;
}

/** The charcoal heading bar's height, which every sheet carrying rows pays for. */
export function printHeadingHeightPoints(options: PrintTableOptions = {}): number {
  const metrics = options.metrics ?? PRINT_TABLE_METRICS;
  return metrics.headPoints * metrics.lineHeight * metrics.headingLines + metrics.headPaddingPoints * 2;
}

/** The characters a column holds on one line: its own figure, or the width the fixed columns left. */
export function printColumnCharactersPerLine(column: PrintLineColumn, columns: readonly PrintLineColumn[], regionWidthInches: number, options: PrintTableOptions = {}): number {
  const metrics = options.metrics ?? PRINT_TABLE_METRICS;
  const drawn = printDrawnColumns(columns);
  const flexible = printFlexibleColumn(columns);
  const fixedCharacters = (name: PrintLineColumn) => PRINT_COLUMN_CHARACTERS[name] ?? PRINT_DEFAULT_COLUMN_CHARACTERS;
  // The description inside the merged Item cell draws smaller, so more of it fits one line than of anything else.
  const characterPoints = (column === 'item' && flexible === 'item' ? metrics.subPoints : metrics.bodyPoints) * metrics.characterWidthRatio;
  /**
   * 🔒 A column the template gave a share of the width holds what that share
   * holds (decision 0656), measured at the size the column draws in, so the
   * split and the drawn table read one width.
   */
  const share = options.widths?.[column];
  if (typeof share === 'number') {
    const points = (share / 100) * regionWidthInches * POINTS_PER_INCH - metrics.cellSidePaddingPoints * 2;
    const perCharacter = column === flexible ? characterPoints : metrics.bodyPoints * metrics.characterWidthRatio;
    return Math.max(1, Math.floor(points / perCharacter));
  }
  if (column !== flexible) return Math.max(1, fixedCharacters(column));
  const fixedPoints = drawn
    .filter((name) => name !== flexible)
    .reduce((sum, name) => sum + fixedCharacters(name) * metrics.bodyPoints * metrics.characterWidthRatio + metrics.cellSidePaddingPoints * 2, 0);
  const flexiblePoints = regionWidthInches * POINTS_PER_INCH - fixedPoints - metrics.cellSidePaddingPoints * 2;
  return Math.max(1, Math.floor(flexiblePoints / characterPoints));
}

/** How many characters of a description fit one line of the cell that carries it, at Region 8's own width. */
export function printDescriptionCharactersPerLine(regionWidthInches: number, columns: readonly PrintLineColumn[], options: PrintTableOptions = {}): number {
  return printColumnCharactersPerLine(printFlexibleColumn(columns) ?? 'description', columns, regionWidthInches, options);
}

/**
 * The lines one row draws on: the tallest of its drawn cells, each measured
 * against the characters its own column holds.
 *
 * 🔨 Every drawn column is measured, not the description alone. The Field
 * family, the statement and the receipt draw a description in a column of its
 * own, and a part number long enough wraps in its narrow column; a row measured
 * on the merged Item cell alone counted every one of those as one line.
 *
 * 🔨 A description that wraps takes more, which is the reason the estimate
 * counts lines rather than rows. Forty short lines and forty wrapped ones do
 * not fill the same region, and a split that assumed they did would clip the
 * difference at the region's foot.
 */
export function printRowLineCount(line: PrintLine, columns: readonly PrintLineColumn[], regionWidthInches: number, options: PrintTableOptions = {}): number {
  // 🔒 A group divider is one band, one line tall (decision 0650).
  if (line.divider !== undefined) return 1;
  const metrics = options.metrics ?? PRINT_TABLE_METRICS;
  const drawn = printDrawnColumns(columns);
  const merged = printMergesDescription(columns);
  let lines = 1;
  for (const column of drawn) {
    const perLine = printColumnCharactersPerLine(column, columns, regionWidthInches, options);
    let cell: number;
    if (column === 'item') {
      // 🔒 The Item cell stacks the name, the SKU, a purchase line's vendor part and the description, each counted by the lines it wraps to (decisions 0640, 0641).
      const description = merged ? line.description ?? '' : '';
      const wrapped = (text: string, characters: number) => (text === '' ? 0 : Math.ceil(text.length / characters));
      // 🔨 The name draws at body size, so fewer of its characters fit a line than of the smaller lines beneath it; a long name wraps and is counted as wrapping (review, 2026-09-23).
      const flexibleItem = printFlexibleColumn(columns) === 'item';
      const nameCharacters = flexibleItem ? Math.max(1, Math.floor((perLine * metrics.subPoints) / metrics.bodyPoints)) : perLine;
      const sku = line.sku ?? '';
      const vendorPart = line.vendorPart ?? '';
      const stacked =
        wrapped(line.item ?? '', nameCharacters) +
        wrapped(sku === '' ? '' : `${t('item.sku')} ${sku}`, perLine) +
        wrapped(vendorPart === '' ? '' : `${t('document.print.vendorPart')} ${vendorPart}`, perLine);
      // A line carrying a description and nothing else draws the description as the line itself, on no second line.
      cell = Math.max(1, stacked + wrapped(description, perLine));
    } else {
      // 🔒 A taxable line's Amount carries a small T beside the figure (decision 1062), and every Amount on a document carrying one keeps the mark's room, two characters more of the cell's width.
      const value = column === 'amount' && (line.taxable === true || options.taxLegend === true) ? `${line[column] ?? ''} T` : line[column] ?? '';
      cell = value === '' ? 1 : Math.ceil(value.length / perLine);
      // 🔨 The Qty cell stacks the unit under the quantity, so a line carrying one draws a line more — unless the template hides it (decision 0656).
      if (column === 'qty' && (line.unit ?? '') !== '' && options.showUnit !== false) cell += 1;
      // 🔒 The Unit Price cell stacks a sales line's list price beneath it the same way (decision 1063).
      if (column === 'unitPrice' && (line.listPrice ?? '') !== '' && options.showListPrice !== false) cell += 1;
    }
    lines = Math.max(lines, cell);
  }
  return lines;
}

/**
 * The height one row draws at, every line of it measured at body size.
 *
 * 🔨 A second line is smaller only where it is a description or a unit. A
 * manufacturer's part number wrapping in its own narrow column is two body
 * lines, and that is the taller cell, so the estimate takes the taller reading
 * and ends a sheet early rather than clipping a row.
 */
export function printRowHeightPoints(lineCount: number, options: PrintTableOptions = {}): number {
  const metrics = options.metrics ?? PRINT_TABLE_METRICS;
  return metrics.cellPaddingPoints * 2 + Math.max(1, lineCount) * metrics.bodyPoints * metrics.lineHeight + metrics.hairlinePoints;
}

/**
 * 🔒 The taxable legend's own height beneath the table (decision 1062): one
 * line of the smaller type and a cell's padding above it.
 */
export function printTaxLegendHeightPoints(options: PrintTableOptions = {}): number {
  const metrics = options.metrics ?? PRINT_TABLE_METRICS;
  return metrics.subPoints * metrics.lineHeight + metrics.cellPaddingPoints;
}

/** The height a region gives the rows, which is the region less the heading bar it repeats. */
export function printRowsHeightPoints(regionHeightInches: number, options: PrintTableOptions = {}): number {
  return regionHeightInches * POINTS_PER_INCH - printHeadingHeightPoints(options);
}

/**
 * The heights the split is measured against.
 *
 * 🔒 A sheet that is not the last runs its table down to the bottom margin,
 * whatever depth the region was seeded at (decision 0568). The room beneath the
 * table is held for the totals and the terms, and those draw on the last sheet
 * alone, so holding it on every sheet prints white paper where lines belong.
 */
export interface PrintSheetHeights {
  /** The seeded depth, which the last sheet keeps so the totals land at their own Y. */
  firstInches: number;
  continuationInches: number;
  /** The depth a sheet that is not the last runs to, which is down to the bottom margin. */
  firstFullInches: number;
  continuationFullInches: number;
}

/**
 * 🔒 The split itself (decision 0568): rows are laid greedily at the deep
 * measurement, the sheet they run out on is fitted again at the seeded depth,
 * and what no longer fits spills onto one more sheet, which is then the last and
 * is fitted the same way. Every sheet repeats the column headings, so every
 * sheet pays for the heading bar.
 *
 * 🔨 A sheet always takes at least one row. A row taller than the region it is
 * drawn in would otherwise start a sheet it never ends, and a clipped row on a
 * sheet of its own is a page somebody can read.
 */
export function printLineSheets(
  lines: readonly PrintLine[],
  columns: readonly PrintLineColumn[],
  region: { widthInches: number; heights: PrintSheetHeights },
  options: PrintTableOptions = {},
): PrintLine[][] {
  if (lines.length === 0) return [[]];
  const heights = region.heights;
  // 🔒 The last sheet holds room for the taxable legend beneath its table where the document carries one (decision 1062).
  const legend = options.taxLegend === true ? printTaxLegendHeightPoints(options) : 0;
  const capacity = (index: number, last: boolean): number =>
    printRowsHeightPoints(index === 0 ? (last ? heights.firstInches : heights.firstFullInches) : last ? heights.continuationInches : heights.continuationFullInches, options) - (last ? legend : 0);
  // How many of the rows from `from` fit a region of this many points, never fewer than one.
  const fits = (from: number, available: number): number => {
    let used = 0;
    let taken = 0;
    for (let index = from; index < lines.length; index += 1) {
      const height = printRowHeightPoints(printRowLineCount(lines[index]!, columns, region.widthInches, options), options);
      if (taken > 0 && used + height > available) break;
      used += height;
      taken += 1;
    }
    return taken;
  };
  const sheets: PrintLine[][] = [];
  let from = 0;
  while (from < lines.length) {
    const index = sheets.length;
    const deep = fits(from, capacity(index, false));
    // 🔨 A sheet the rows run out on is a candidate last sheet, so it is fitted again at the seeded depth.
    const taken = from + deep >= lines.length ? fits(from, capacity(index, true)) : deep;
    sheets.push(lines.slice(from, from + taken));
    from += taken;
  }
  return sheets;
}

/**
 * 🔒 The running header every sheet after a document's first carries, in place
 * of the letterhead and the envelope's regions (T274; decision 0772). The owner:
 * *"the second is type header: Company Name left, Terms title centre, Document
 * Number right."* It stands at the top margin across the page's width, one line
 * of the template's body type over a hairline, and a continued sheet's table or
 * a terms sheet's words begin the gap beneath it.
 *
 * 🔨 One declaration, read by the split and by the drawing alike, so the rows a
 * continued sheet is measured for are the rows it has room to draw.
 */
export const PRINT_RUNNING_HEADER = {
  /** The band's own depth: one line of body type and its hairline. */
  heightInches: 0.3,
  /** The white between the band's hairline and what the sheet carries beneath it. */
  gapInches: 0.15,
} as const;

/** Where a sheet after the first begins its table or its terms (decision 0772): beneath the running header at the template's top margin. */
export function printRunningHeaderFootInches(page: { marginTopInches: number }): number {
  return page.marginTopInches + PRINT_RUNNING_HEADER.heightInches + PRINT_RUNNING_HEADER.gapInches;
}

/**
 * 🔒 The terms print on sheets of their own, after the document's last sheet
 * (T224; decision 0705). The owner: *"terms and conditions go on next page of
 * accounting document and continue on till no more pages needed."* The terms are
 * cut into pieces here — a section's heading, a clause's place and title, and
 * each block of its words — and laid onto as many sheets as they need, by the
 * same kind of server-side estimate the line table's split is.
 */
export type PrintTermsUnit =
  | { kind: 'heading'; text: string }
  | { kind: 'title'; text: string }
  | { kind: 'text'; html: string }
  /** 🔒 The *Terms as sent* line a sent document's terms lead with (T277; decision 0773). */
  | { kind: 'note'; text: string };

/**
 * 🔒 The terms' own spacing, in one place: the renderer's rules read these, and
 * so does the estimate, so the split and the drawing agree (decision 0705).
 */
export const PRINT_TERMS_METRICS = {
  /** A heading is a step larger than the words. */
  headingScale: 1.2,
  /** The space a heading takes above and below it together. */
  headingGapPoints: 8,
  /** The space a clause's place and title take above and below together. */
  titleGapPoints: 4,
  /** The space beneath a paragraph or a list. */
  blockGapPoints: 3,
  /** How far a list is indented. */
  listIndentPoints: 14,
  /** The line height the words draw at, the sheet's own. */
  lineHeight: PRINT_TABLE_METRICS.lineHeight,
  /** An average character's width as a share of its own size: the line table's own figure, kept cautious. */
  characterWidthRatio: 0.52,
} as const;

/**
 * 🔒 The terms in order as the sheet draws them (decisions 0702, 0704, 0705):
 * the Preamble's blocks, then each section's heading and each clause's place
 * and title before the blocks of its words; or the document's own text, block by
 * block. Every block is sanitised here, whatever was stored.
 */
export function printTermsUnits(terms: PrintTerms | null | undefined): PrintTermsUnit[] {
  if (!terms) return [];
  const units = termsBodyUnits(terms);
  // 🔒 A sent document's terms lead with the day they went out, and terms with no words carry no line (decision 0773).
  return units.length > 0 && terms.asSent ? [{ kind: 'note', text: terms.asSent }, ...units] : units;
}

function termsBodyUnits(terms: PrintTerms): PrintTermsUnit[] {
  const text = (html: string): PrintTermsUnit[] => richTextBlocks(sanitizeRichText(html)).map((block) => ({ kind: 'text', html: block }));
  // 🔒 The document's own text, then any section a confidentiality role added, which no own text removes (decision 1171).
  const units: PrintTermsUnit[] = terms.override !== null ? text(terms.override) : terms.preamble ? text(terms.preamble) : [];
  const clauseUnits = (clause: PrintTermsClause): PrintTermsUnit[] => {
    const title = `${clause.position} ${clause.title}`.trim();
    // 🔒 A sub-section prints its place and title as a heading, then its own clauses (T273; decision 0771); one with none prints nothing.
    if (clause.clauses) return clause.clauses.length === 0 ? [] : [{ kind: 'heading', text: title }, ...clause.clauses.flatMap(clauseUnits)];
    return [...(title ? [{ kind: 'title' as const, text: title }] : []), ...text(clause.body)];
  };
  for (const section of terms.sections) {
    if (section.clauses.length === 0) continue;
    units.push({ kind: 'heading', text: section.heading });
    for (const clause of section.clauses) units.push(...clauseUnits(clause));
  }
  return units;
}

/** The characters a line of the terms holds at this size and width. */
function termsCharactersPerLine(widthPoints: number, pointSize: number): number {
  return Math.max(1, Math.floor(widthPoints / (pointSize * PRINT_TERMS_METRICS.characterWidthRatio)));
}

/**
 * The height one piece draws at, in points: each line it wraps to at the size
 * it draws in, and the space around it. 🔨 A heading inside a clause's words and
 * a list's indent are counted, so a sheet ends early rather than clipping.
 */
export function printTermsUnitHeightPoints(unit: PrintTermsUnit, widthInches: number, pointSize: number): number {
  const metrics = PRINT_TERMS_METRICS;
  const widthPoints = widthInches * POINTS_PER_INCH;
  const lines = (text: string, size: number, width: number) =>
    text.split('\n').reduce((sum, line) => sum + Math.max(1, Math.ceil(line.length / termsCharactersPerLine(width, size))), 0);
  if (unit.kind === 'heading') return lines(unit.text, pointSize * metrics.headingScale, widthPoints) * pointSize * metrics.headingScale * metrics.lineHeight + metrics.headingGapPoints;
  if (unit.kind === 'title' || unit.kind === 'note') return lines(unit.text, pointSize, widthPoints) * pointSize * metrics.lineHeight + (unit.kind === 'note' ? metrics.headingGapPoints : metrics.titleGapPoints);
  const heading = /^<h[1-4]\b/i.test(unit.html);
  const list = /^<(ul|ol)\b/i.test(unit.html);
  const size = heading ? pointSize * metrics.headingScale : pointSize;
  const width = list ? widthPoints - metrics.listIndentPoints : widthPoints;
  return lines(richTextPlain(unit.html), size, width) * size * metrics.lineHeight + (heading ? metrics.headingGapPoints : metrics.blockGapPoints);
}

/**
 * 🔒 The terms laid onto sheets (decision 0705): greedily, each sheet taking
 * what fits the room below the repeated header band. 🔨 A heading or a clause's
 * title never ends a sheet — it moves over with the words it heads — and a
 * sheet always takes at least one piece, so a block taller than a sheet is
 * clipped at the foot of a sheet of its own rather than never ending.
 */
export function printTermsSheets(units: readonly PrintTermsUnit[], area: { widthInches: number; heightInches: number }, pointSize: number): PrintTermsUnit[][] {
  const available = area.heightInches * POINTS_PER_INCH;
  const sheets: PrintTermsUnit[][] = [];
  let from = 0;
  while (from < units.length) {
    let used = 0;
    let to = from;
    while (to < units.length) {
      const height = printTermsUnitHeightPoints(units[to]!, area.widthInches, pointSize);
      if (to > from && used + height > available) break;
      used += height;
      to += 1;
    }
    // 🔨 A heading or a title left last on a sheet goes over with what it heads.
    if (to < units.length) {
      let end = to;
      while (end > from && units[end - 1]!.kind !== 'text') end -= 1;
      if (end > from) {
        to = end;
      } else {
        // The sheet holds nothing but the heading and title of a block that does
        // not fit beside them: they stay, and the block comes with them, clipped at
        // the foot, rather than a sheet carrying a heading alone (review, 2026-09-24).
        while (to < units.length && units[to]!.kind !== 'text') to += 1;
        if (to < units.length) to += 1;
      }
    }
    sheets.push(units.slice(from, to));
    from = to;
  }
  return sheets;
}
